import { useContext, useMemo, useState } from "react";

import {
  FormType,
  PaymentMethod,
  FrontendRoutes,
  RecaptchaGuardMessage,
  parseFormBackgroundTheme,
} from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { enqueueSnackbar } from "notistack";

import { FrontendFormContext } from "../../../../../../common/contexts/FrontendFormContext";
import { useLocaleContext } from "../../../../../../common/contexts/LocaleContext";
import { StripeContext } from "../../../../../../common/contexts/StripeContext";
import { useUserInputContext } from "../../../../../../common/contexts/UserInputContext";
import { serializeProductBids } from "../../../../../../common/helpers/command";
import { getThankYouPageUrl } from "../../../../../../common/helpers/thankYouPage";
import { usePaymentErrorContext } from "../../../blocks/payment/PaymentSection/PaymentElement/PaymentErrorContext";
import { useAmplitudePaymentEventLog } from "../../../blocks/payment/PaymentSection/PaymentElement/useAmplitudePaymentEventLog";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { sendEmbedLoadingMessage } from "@/canvas/features/FormV2/helpers/dynamicEmbedMessaging";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { AppRouterType, trpc } from "@/common/helpers/trpc";
import { useIsEmbeddedModal } from "@/common/hooks/useEmbeddedModal";
import { useFormQueryParams } from "@/common/hooks/useFormParams";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getEvent } from "@/components/PaymentProcessor/helper";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];
type Command = NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];

export const useSubmitNewBids = () => {
  const { t } = useTranslate();
  const { formObject: ticketingForm, form } = useFormV2Context(FormType.Ticketing);
  const { mutateAsync: submitNewBids } = trpc.form_submitNewBids.useMutation();
  const { executeTrpc } = useRecaptchaRetry();
  const { mutateAsync: logCommandUserInteractionMutation } = trpc.form_logCommandUserInteraction.useMutation();

  const router = useRouter();
  const { locale, isoLocale } = useLocaleContext();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const isEmbeddedModal = useIsEmbeddedModal(query);

  const {
    localeField: ticketingField,
    selectedOccurrenceWithRates,
    isAuction,
    formObject: ticketingData,
  } = useFormV2Context(FormType.Ticketing);
  const { expressCheckoutWallet } = useContext(StripeContext);
  const { impactChainUtmSourceEmail, source } = useFormQueryParams();
  const {
    commandId,
    selectedPaymentMethod,
    postTransactionUrl,
    generateETicket,
    selectedTip,
    shouldSendTipPercentage,
    tip,
    formData,
    category,
    organization,
    themeColor,
    isEmbed,
    isEmbedV2,
    embedId,
    displayedFormAmount,
    formAmplitudeContext,
    getTipChoice,
    displayAddressQuestion,
    currency,
  } = useContext(FrontendFormContext);
  const { userInputs } = useUserInputContext();

  const amplitudeEventLogModel = useMemo(
    () => ({
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketing: {
        extraDonation: ticketingData.extraDonation,
        thermometerTarget: ticketingData.thermometerTarget,
      },
      itemsCount: 0,
      addOnsCount: 0,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet,
      formObject: ticketingForm,
    }),
    [
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketingData.extraDonation,
      ticketingData.thermometerTarget,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet,
      ticketingForm,
    ]
  );
  const { logDonorFormSubmitted } = useAmplitudePaymentEventLog({
    formStateErrors: form.formState.errors,
    formObject: ticketingForm,
    model: amplitudeEventLogModel,
  });
  const { handlePaymentError } = usePaymentErrorContext();

  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const handlePaymentFailed = ({ error }: { error?: { message: string; code: string } }) => {
    const code = error?.code;
    const translationKey = code ? (`error.${code}` as const) : null;

    const translated = translationKey ? t("common", translationKey as never) : null;
    const hasTranslation = Boolean(translated) && translated !== translationKey;
    const displayedMessage =
      code === RecaptchaGuardMessage
        ? t("common", "error.recaptchaError")
        : (hasTranslation ? translated : error?.message) || t("common", "unknownError", { code: 150 });

    enqueueSnackbar(displayedMessage, { vibe: "danger" });

    handlePaymentError({
      rawErrorCode: code || "new_bids_unknown_error",
      rawErrorMessage: displayedMessage,
      step: "server-submit",
      paymentMethod: selectedPaymentMethod ?? PaymentMethod.Card,
      isExpressCheckout: false,
      isKnownError: Boolean(code),
    });
    setPaymentInProgress(false);
  };

  const handlePaymentSucceededFinalized = async (command: Command) => {
    const formType = FormType.Ticketing;
    const event =
      ticketingField &&
      getEvent({
        ticketing: ticketingForm,
        ticketingField,
        selectedOccurrence: selectedOccurrenceWithRates?.occurrence,
      });

    logDonorFormSubmitted({
      isExpressCheckout: false,
      isPaymentSucceeded: true,
      extra: { re_bid: true },
    });

    return getThankYouPageUrl({
      command,
      formType,
      organization,
      postTransactionUrl,
      isEmbeddedModal,
      generateETicket,
      event,
      formData,
      isoLocale,
      category,
      isAuction,
      themeColor,
      isDarkMode: ticketingForm.isDarkMode || false,
      backgroundTheme: parseFormBackgroundTheme(ticketingForm.backgroundTheme),
      isEmbed,
      embedId,
    });
  };

  const submitNewBidsWithInput = async (paymentInput: TicketingPaymentInput) => {
    setPaymentInProgress(true);

    const productBids = serializeProductBids(paymentInput.ticketsPurchased, paymentInput.customAnswersOfParticipants);
    const tipPercentage = shouldSendTipPercentage ? selectedTip.percentage || 0 : 0;
    const tipAmount = shouldSendTipPercentage ? 0 : tip;

    if (!productBids?.length) {
      setPaymentInProgress(false);
      return;
    }

    const { data, error } = await executeTrpc(RecaptchaAction.SubmitNewBids, undefined, submitNewBids, {
      productBids,
      tipPercentage,
      tipAmount,
      ticketingId: ticketingForm.id,
    });
    if (error && !data) {
      handlePaymentFailed({ error });
      return;
    }

    if (userInputs) {
      await logCommandUserInteractionMutation({ commandId: data?.id!, payload: userInputs });
    }
    const url = await handlePaymentSucceededFinalized(data!);
    if (url) {
      if (isEmbedV2 && url.includes(FrontendRoutes.EmbedThankYou)) {
        sendEmbedLoadingMessage();
      }
      await router.push(url);
    }
    setPaymentInProgress(false);
  };

  return { submitNewBidsWithInput, paymentInProgress };
};
